import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Table, Drawer, SearchBar, BadgePill, EmptyState } from '@/components/campus';
import {
  GraduationCap,
  Plus,
  Building2,
  Calendar,
  Users,
  QrCode,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Loader2,
  MapPin,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Ban,
  RefreshCw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

// ==================== CAMPUS DETAIL DRAWER ====================
const CampusDetailDrawer = ({ campus, isOpen, onClose }) => {
  if (!campus) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Campus Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <GraduationCap className="w-8 h-8 text-electric-ice-blue" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-cloud-white">{campus.name}</h3>
            {campus.location && (
              <p className="text-soft-silver flex items-center gap-1 mt-1">
                <MapPin className="w-4 h-4" />
                {campus.location}
              </p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <Building2 className="w-6 h-6 text-cool-mint mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(campus.organization_count)}</p>
            <p className="text-xs text-muted-text">Organizations</p>
          </div>
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <Calendar className="w-6 h-6 text-electric-ice-blue mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(campus.event_count)}</p>
            <p className="text-xs text-muted-text">Events</p>
          </div>
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <QrCode className="w-6 h-6 text-soft-amber mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(campus.checkin_count)}</p>
            <p className="text-xs text-muted-text">Check-ins</p>
          </div>
        </div>

        {/* Organizations List */}
        <div>
          <h4 className="text-sm font-medium text-soft-silver mb-3">Top Organizations</h4>
          <div className="space-y-2">
            {campus.organizations?.length > 0 ? (
              campus.organizations.slice(0, 5).map((org, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
                  <span className="text-cloud-white">{org.name}</span>
                  <span className="text-xs text-muted-text font-mono">{org.event_count} events</span>
                </div>
              ))
            ) : (
              <p className="text-muted-text text-sm text-center py-4">No organizations yet</p>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

// ==================== ADD CAMPUS DRAWER ====================
const AddCampusDrawer = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Campus name is required');
      return;
    }
    onSubmit({ name, location });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Add Campus" width="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-soft-silver">Campus Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., UCLA, Stanford, USC"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-soft-silver">Location</Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., Los Angeles, CA"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>

        <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 border-white/[0.08]"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Campus'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

// ==================== EDIT CAMPUS DRAWER ====================
const EditCampusDrawer = ({ campus, isOpen, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');

  useEffect(() => {
    if (campus) {
      setName(campus.name || '');
      setLocation(campus.location || '');
    }
  }, [campus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Campus name is required');
      return;
    }
    onSubmit({ id: campus.id, name, location });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Edit Campus" width="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-soft-silver">Campus Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., UCLA, Stanford, USC"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-soft-silver">Location</Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., Los Angeles, CA"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>

        <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1 border-white/[0.08]"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

// ==================== MAIN CAMPUSES PAGE ====================
export const Campuses = () => {
  const { session } = useAuth();
  
  const [campuses, setCampuses] = useState([]);
  const [totalCampuses, setTotalCampuses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Fetch campuses with pagination
  const fetchCampuses = useCallback(async (page = 1, search = '') => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      if (search) params.append('search', search);
      
      const res = await fetch(`${API_URL}/api/platform/campuses?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setCampuses(data.campuses || []);
        setTotalCampuses(data.total || data.campuses?.length || 0);
      }
    } catch (error) {
      console.error('Failed to fetch campuses:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch when debounced search or page changes
  useEffect(() => {
    setCurrentPage(1);  // Reset to page 1 on new search
  }, [debouncedSearch]);

  useEffect(() => {
    fetchCampuses(currentPage, debouncedSearch);
  }, [fetchCampuses, currentPage, debouncedSearch]);

  const totalPages = Math.ceil(totalCampuses / pageSize);

  // Handle add campus
  const handleAddCampus = async (data) => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/campuses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (res.ok) {
        toast.success('Campus created successfully');
        setIsAddOpen(false);
        fetchCampuses(currentPage, searchQuery);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create campus');
      }
    } catch (error) {
      toast.error('Failed to create campus');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit campus
  const handleEditCampus = async (data) => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/campuses/${data.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: data.name, location: data.location }),
      });
      
      if (res.ok) {
        toast.success('Campus updated successfully');
        setIsEditOpen(false);
        setSelectedCampus(null);
        fetchCampuses(currentPage, searchQuery);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update campus');
      }
    } catch (error) {
      toast.error('Failed to update campus');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete campus
  const handleDeleteCampus = async (campus) => {
    if (!window.confirm(`Are you sure you want to delete "${campus.name}"?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/platform/campuses/${campus.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (res.ok) {
        toast.success('Campus deleted successfully');
        fetchCampuses(currentPage, searchQuery);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete campus');
      }
    } catch (error) {
      toast.error('Failed to delete campus');
    }
  };

  // Handle deactivate campus
  const handleDeactivateCampus = async (campus) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/campuses/${campus.id}/deactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Campus deactivated');
        fetchCampuses(currentPage, searchQuery);
      } else {
        toast.error(data.detail || 'Failed to deactivate campus');
      }
    } catch (error) {
      toast.error('Failed to deactivate campus');
    }
  };

  // Handle reactivate campus
  const handleReactivateCampus = async (campus) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/campuses/${campus.id}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Campus reactivated');
        fetchCampuses(currentPage, searchQuery);
      } else {
        toast.error(data.detail || 'Failed to reactivate campus');
      }
    } catch (error) {
      toast.error('Failed to reactivate campus');
    }
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Campus',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-electric-ice-blue" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.name}</p>
            {row.location && <p className="text-xs text-muted-text">{row.location}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'organization_count',
      label: 'Organizations',
      align: 'center',
      render: (value) => (
        <span className="font-mono text-cloud-white">{formatNumber(value)}</span>
      ),
    },
    {
      key: 'event_count',
      label: 'Events',
      align: 'center',
      render: (value) => (
        <span className="font-mono text-cloud-white">{formatNumber(value)}</span>
      ),
    },
    {
      key: 'checkin_count',
      label: 'Check-ins',
      align: 'center',
      render: (value) => (
        <span className="font-mono text-cool-mint">{formatNumber(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => { setSelectedCampus(row); setIsDetailOpen(true); }}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => { setSelectedCampus(row); setIsEditOpen(true); }}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Campus
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/[0.08]" />
            {row.is_active !== false ? (
              <DropdownMenuItem 
                onClick={() => handleDeactivateCampus(row)}
                className="text-soft-amber hover:bg-soft-amber/10"
              >
                <Ban className="w-4 h-4 mr-2" />
                Deactivate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                onClick={() => handleReactivateCampus(row)}
                className="text-fresh-mint hover:bg-fresh-mint/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reactivate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem 
              onClick={() => handleDeleteCampus(row)}
              className="text-signal-red hover:bg-signal-red/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Campus
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading campuses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campuses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">Campuses</h1>
          <p className="text-soft-silver text-sm mt-1">{totalCampuses.toLocaleString()} campuses on the platform</p>
        </div>
        <Button
          onClick={() => setIsAddOpen(true)}
          className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Campus
        </Button>
      </div>

      {/* Search */}
      <SearchBar
        placeholder="Search campuses..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      {/* Table */}
      <Table
        columns={columns}
        data={campuses}
        emptyState={
          <div className="py-12">
            <EmptyState
              icon="events"
              title="No campuses found"
              description={searchQuery ? "Try a different search term" : "Add campuses to get started."}
              actionLabel="Add Campus"
              onAction={() => setIsAddOpen(true)}
            />
          </div>
        }
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
          <p className="text-sm text-soft-silver">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCampuses)} of {totalCampuses.toLocaleString()} campuses
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="border-white/[0.08]"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <span className="text-sm text-soft-silver px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="border-white/[0.08]"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <CampusDetailDrawer
        campus={selectedCampus}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />

      {/* Edit Drawer */}
      <EditCampusDrawer
        campus={selectedCampus}
        isOpen={isEditOpen}
        onClose={() => { setIsEditOpen(false); setSelectedCampus(null); }}
        onSubmit={handleEditCampus}
        isLoading={isSubmitting}
      />

      {/* Add Drawer */}
      <AddCampusDrawer
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSubmit={handleAddCampus}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default Campuses;
